/**
 * Tenant Context Provider
 *
 * Provides tenant configuration to the application and applies
 * brand theming via CSS custom properties.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  TenantConfig,
  TenantResolution,
  FeatureFlags,
  CrisisConfig,
  BrandConfig,
} from '../lib/tenant/types';
import { DEFAULT_TENANT } from '../lib/tenant/types';
import { resolveTenant } from '../lib/tenant/resolver';
import { injectPWAMeta, notifyServiceWorkerOfTenantChange } from '../lib/tenant/manifest';

// ============================================
// CONTEXT TYPES
// ============================================

interface TenantContextValue {
  // Tenant state
  tenant: TenantConfig;
  resolution: TenantResolution;
  isLoading: boolean;
  error: Error | null;

  // Convenient accessors
  brand: BrandConfig;
  features: FeatureFlags;
  crisis: CrisisConfig;

  // Feature checks
  isFeatureEnabled: (feature: keyof FeatureFlags) => boolean;

  // Branding helpers
  getBrandColor: (key: keyof BrandConfig['colors']) => string;
  getLogo: (variant?: 'primary' | 'icon') => string;

  // Actions
  refreshTenant: () => Promise<void>;
}

// ============================================
// CONTEXT
// ============================================

const TenantContext = createContext<TenantContextValue | null>(null);

// ============================================
// THEME APPLICATION
// ============================================

/**
 * Apply tenant brand colors as CSS custom properties
 */
function applyBrandTheme(brand: BrandConfig): void {
  const root = document.documentElement;

  // Primary colors
  root.style.setProperty('--brand-primary', brand.colors.primary);
  root.style.setProperty('--brand-primary-hover', brand.colors.primaryHover);
  root.style.setProperty('--brand-primary-text', brand.colors.primaryText);

  // Accent colors (with fallbacks)
  if (brand.colors.accentGrowth) {
    root.style.setProperty('--accent-growth', brand.colors.accentGrowth);
  }
  if (brand.colors.accentCalm) {
    root.style.setProperty('--accent-calm', brand.colors.accentCalm);
  }
  if (brand.colors.accentStress) {
    root.style.setProperty('--accent-stress', brand.colors.accentStress);
  }
  if (brand.colors.accentWarning) {
    root.style.setProperty('--accent-warning', brand.colors.accentWarning);
  }

  // Background overrides
  if (brand.colors.bgVoid) {
    root.style.setProperty('--bg-void', brand.colors.bgVoid);
  }
  if (brand.colors.bgSurface) {
    root.style.setProperty('--bg-surface', brand.colors.bgSurface);
  }
  if (brand.colors.bgElevated) {
    root.style.setProperty('--bg-elevated', brand.colors.bgElevated);
  }

  // Typography
  if (brand.typography?.fontFamily) {
    root.style.setProperty('--font-family', brand.typography.fontFamily);
  }
  if (brand.typography?.fontFamilyDisplay) {
    root.style.setProperty('--font-family-display', brand.typography.fontFamilyDisplay);
  }
  if (brand.typography?.fontFamilyMono) {
    root.style.setProperty('--font-mono', brand.typography.fontFamilyMono);
  }

  // Browser theme color
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', brand.themeColor);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = brand.themeColor;
    document.head.appendChild(meta);
  }
}

/**
 * Update document title and favicon
 */
function applyBrandMeta(brand: BrandConfig): void {
  // Update document title
  document.title = brand.name;

  // Update favicon
  const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (favicon) {
    favicon.href = brand.logos.icon;
  } else {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = brand.logos.icon;
    document.head.appendChild(link);
  }

  // Update Apple touch icon
  const appleTouchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (appleTouchIcon && brand.logos.icon192) {
    appleTouchIcon.href = brand.logos.icon192;
  }
}

// ============================================
// PROVIDER
// ============================================

interface TenantProviderProps {
  children: ReactNode;
  /**
   * Optional initial tenant config (for SSR or testing)
   */
  initialTenant?: TenantConfig;
  /**
   * API endpoint for fetching tenant config (production use)
   */
  apiEndpoint?: string;
}

export function TenantProvider({
  children,
  initialTenant,
  apiEndpoint,
}: TenantProviderProps): JSX.Element {
  const [resolution, setResolution] = useState<TenantResolution>(() => {
    if (initialTenant) {
      return {
        tenant: initialTenant,
        source: 'default',
      };
    }
    // Resolve on initial render
    return resolveTenant();
  });
  const [isLoading, setIsLoading] = useState(!initialTenant);
  const [error, setError] = useState<Error | null>(null);

  // Resolve tenant on mount
  useEffect(() => {
    if (initialTenant) {
      setIsLoading(false);
      return;
    }

    const resolveAsync = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // If we have an API endpoint, fetch from there
        if (apiEndpoint) {
          const response = await fetch(apiEndpoint);
          if (response.ok) {
            const tenant = await response.json();
            setResolution({
              tenant,
              source: 'hostname', // API-resolved
            });
          } else {
            // Fall back to default
            setResolution(resolveTenant());
          }
        } else {
          // Use local resolution
          setResolution(resolveTenant());
        }
      } catch (err) {
        console.error('[TenantProvider] Error resolving tenant:', err);
        setError(err instanceof Error ? err : new Error('Failed to resolve tenant'));
        // Fall back to default tenant
        setResolution({
          tenant: DEFAULT_TENANT,
          source: 'default',
        });
      } finally {
        setIsLoading(false);
      }
    };

    resolveAsync();
  }, [initialTenant, apiEndpoint]);

  // Apply brand theme when tenant changes
  useEffect(() => {
    if (!isLoading && resolution.tenant) {
      applyBrandTheme(resolution.tenant.brand);
      applyBrandMeta(resolution.tenant.brand);

      // Inject PWA manifest and meta tags
      injectPWAMeta(resolution.tenant);

      // Notify service worker of tenant change for cache management
      notifyServiceWorkerOfTenantChange(resolution.tenant).catch((err) => {
        console.warn('[TenantProvider] Failed to notify service worker:', err);
      });

      console.info('[TenantProvider] Tenant applied:', {
        id: resolution.tenant.id,
        name: resolution.tenant.displayName,
        source: resolution.source,
      });
    }
  }, [isLoading, resolution]);

  // Refresh tenant
  const refreshTenant = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const newResolution = resolveTenant();
      setResolution(newResolution);
    } finally {
      setIsLoading(false);
    }
  };

  // Feature check helper
  const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
    return resolution.tenant.features[feature] ?? false;
  };

  // Brand color helper
  const getBrandColor = (key: keyof BrandConfig['colors']): string => {
    return resolution.tenant.brand.colors[key] ?? '';
  };

  // Logo helper
  const getLogo = (variant: 'primary' | 'icon' = 'primary'): string => {
    const logos = resolution.tenant.brand.logos;
    if (variant === 'icon') {
      return logos.icon;
    }
    return logos.primary;
  };

  // Context value
  const value = useMemo<TenantContextValue>(
    () => ({
      tenant: resolution.tenant,
      resolution,
      isLoading,
      error,

      // Convenient accessors
      brand: resolution.tenant.brand,
      features: resolution.tenant.features,
      crisis: resolution.tenant.crisis,

      // Helpers
      isFeatureEnabled,
      getBrandColor,
      getLogo,

      // Actions
      refreshTenant,
    }),
    [resolution, isLoading, error]
  );

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

/**
 * Access the tenant context
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}

/**
 * Check if a feature is enabled
 */
export function useFeatureFlag(feature: keyof FeatureFlags): boolean {
  const { isFeatureEnabled } = useTenant();
  return isFeatureEnabled(feature);
}

/**
 * Get brand configuration
 */
export function useBrand(): BrandConfig {
  const { brand } = useTenant();
  return brand;
}

/**
 * Get crisis resources configuration
 */
export function useCrisisResources(): CrisisConfig {
  const { crisis } = useTenant();
  return crisis;
}
